import React, { useState } from "react";
import { useTileOperations } from "../hooks/useTileOperations";
import { useMediaUpload } from "../hooks/useMediaUpload";
import { toast } from "react-toastify";

function TileEditor({ onSave, onCancel, editingTile = null }) {
  const [title, setTitle] = useState(editingTile?.title || "");
  const [description, setDescription] = useState(editingTile?.description || "");
  const [mediaType, setMediaType] = useState(editingTile?.mediaType || "image");
  const [mediaFile, setMediaFile] = useState(null);
  const [memoryDate, setMemoryDate] = useState(
    editingTile?.memoryDate 
      ? new Date(editingTile.memoryDate).toISOString().split('T')[0] 
      : ""
  );
  const [error, setError] = useState("");

  // Use the custom hooks
  const { creating, updating, createTile, updateTile } = useTileOperations();
  const { uploading, uploadMedia } = useMediaUpload();

  // Track if we're editing or creating
  const isEditing = !!editingTile;
  const isProcessing = creating || updating || uploading;

  const handleFileChange = (e) => {
    setMediaFile(e.target.files[0]);
    setError(""); // Clear any previous errors
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      let mediaUrl = editingTile?.mediaUrl || "";
      let publicId = editingTile?.publicId || "";
      let finalMediaType = mediaType;

      // Only upload new media if a file is selected
      if (mediaFile) {
        const uploadResponse = await uploadMedia(mediaFile);
        mediaUrl = uploadResponse.asset.url;
        publicId = uploadResponse.asset.publicId;
        finalMediaType = uploadResponse.asset.mediaType;
      } else if (!isEditing) {
        // If creating a new tile, media file is required
        setError("Please select a media file");
        return;
      }

      const tileData = {
        title,
        description,
        mediaType: finalMediaType,
        mediaUrl,
        publicId,
        memoryDate: memoryDate ? new Date(memoryDate).toISOString() : null,
      };

      let response;
      if (isEditing) {
        response = await updateTile(editingTile._id, tileData);
      } else {
        response = await createTile(tileData);
      }

      // Call the parent's onSave callback with the response
      if (onSave) {
        onSave(response);
      }

      // Reset form if creating new tile
      if (!isEditing) {
        setTitle("");
        setDescription("");
        setMediaType("image");
        setMediaFile(null);
        setMemoryDate("");
      }

    } catch (error) {
      console.error("Error saving tile:", error);
      setError(error.error || "Failed to save tile. Please try again.");
      // toast.error(error.error || error.message);
    }
  };

  const handleCancel = () => {
    // Reset form
    setTitle(editingTile?.title || "");
    setDescription(editingTile?.description || "");
    setMediaType(editingTile?.mediaType || "image");
    setMediaFile(null);
    setMemoryDate(
      editingTile?.memoryDate 
        ? new Date(editingTile.memoryDate).toISOString().split('T')[0] 
        : ""
    );
    setError("");
    
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="tile-editor">
      <h2>{isEditing ? "Edit Tile" : "Create New Tile"}</h2>

      {/* {error && (
        <div className="error-message" style={{ color: "red", marginBottom: "1rem" }}>
          {error}
        </div>
      )} */}

      <div className="input-group">
        <label htmlFor="title">Title</label>
        <input
          type="text"
          id="title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Enter tile title"
          required
          disabled={isProcessing}
        />
      </div>

      <div className="input-group">
        <label htmlFor="memoryDate">Memory Date</label>
        <input
          type="date"
          id="memoryDate"
          value={memoryDate}
          onChange={(e) => setMemoryDate(e.target.value)}
          disabled={isProcessing}
          className="border border-[#8A4FFF] rounded-lg p-2 w-full focus:outline-none ring-[#8A4FFF] focus:ring-2 focus:ring-purple-500"
        />
      </div>

      <div className="input-group">
        <label htmlFor="mediaType">Media Type</label>
        <select
          id="mediaType"
          value={mediaType}
          onChange={(e) => setMediaType(e.target.value)}
          disabled={isProcessing}
        >
          <option value="image">Image</option>
          <option value="video">Video</option>
          {/* <option value="audio">Audio</option> */}
        </select>
      </div>

      <div className="input-group">
        <label htmlFor="mediaFile">
          {mediaType === "image" ? "Image" : mediaType === "video" ? "Video" : "Audio"} File
          {isEditing && " (Leave empty to keep current media)"}
        </label>
        <div className="file-input-wrapper">
          <input
            type="file"
            id="mediaFile"
            accept={
              mediaType === "image" 
                ? "image/*" 
                : mediaType === "video" 
                  ? "video/*" 
                  : "audio/*"
            }
            onChange={handleFileChange}
            required={!isEditing}
            disabled={isProcessing}
            className="hidden-file-input"
          />
          <label 
            htmlFor="mediaFile" 
            className="file-input-button"
            style={{
              display: 'inline-block',
              padding: '10px 20px',
              backgroundColor: 'rgba(138, 79, 255, 0.2)',
              border: '1px solid #8A4FFF',
              borderRadius: '5px',
              color: '#8A4FFF',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              opacity: isProcessing ? 0.5 : 1
            }}
            onMouseEnter={(e) => {
              if (!isProcessing) {
                e.target.style.backgroundColor = 'rgba(138, 79, 255, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'rgba(138, 79, 255, 0.2)';
            }}
          >
            Browse...
          </label>
          {mediaFile && (
            <span style={{ marginLeft: '10px', color: '#8A4FFF' }}>
              {mediaFile.name}
            </span>
          )}
        </div>
        {uploading && (
          <div className="upload-status">
            <span>Uploading media...</span>
          </div>
        )}
        {isEditing && editingTile.mediaUrl && !mediaFile && (
          <div className="current-media">
            <span>Current: {editingTile.mediaType} file</span>
          </div>
        )}
      </div>

      <div className="grow--wrap">
        {/* <label htmlFor="description">Description</label> */}
        <textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Enter description"
          required
          name="description"
          className="border border-[#8A4FFF] rounded-lg p-2 w-full h-24 resize-none focus:outline-none ring-[#8A4FFF] focus:ring-2 focus:ring-purple-500" 
          onInput={(e) => {
            if (e.target.parentNode) {
              e.target.parentNode.dataset.replicatedValue = e.target.value;
            }
          }}
          disabled={isProcessing}
        />
      </div>

      <div className="editor-buttons">
        <button 
          type="button" 
          className="cancel-button" 
          onClick={handleCancel}
          disabled={isProcessing}
        >
          Cancel
        </button>
        <button 
          type="submit" 
          className="save-button"
          disabled={isProcessing}
        >
          {isProcessing 
            ? (uploading ? "Uploading..." : isEditing ? "Updating..." : "Creating...") 
            : (isEditing ? "Update" : "Save")
          }
        </button>
      </div>
    </form>
  );
}

export default TileEditor;